import { Component, ChangeDetectionStrategy, Inject } from '@angular/core';
import { FormGroup, FormControl } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { filter, Observable, of, tap } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { StaticMessage, dynamicTextPlaceholder } from '@shared/enums';
import {
  BaseDialogComponent,
  DeactivationGuardedComponent,
} from '@shared/models/component';
import { DialogComponent, DialogContext, DialogData } from '@grid/models';
import { canDeactivateUnsavedChanges } from '@shared/guards';

@UntilDestroy()
@Component({
  selector: 'app-comment-dialog-edit',
  templateUrl: './comment-dialog-edit.component.html',
  styleUrls: ['./comment-dialog-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CommentDialogEditComponent
  extends BaseDialogComponent
  implements DialogComponent<any>
{
  context: DialogContext<any>;
  form = new FormGroup({
    comment: new FormControl<string | null>(null),
  });

  staticMessage = StaticMessage;
  title: string = $localize`:@@shared.commentModal.titleComment:(---): Comment`;

  //TODO: this should be removed once the DeactivationGuardedComponent refactor is done
  private _prevGuardedComponent: DeactivationGuardedComponent;

  constructor(
    public dialogRef: MatDialogRef<CommentDialogEditComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData<any>
  ) {
    super(dialogRef);

    this.title = this.title.replace(
      dynamicTextPlaceholder,
      data.context.additionalData.title ?? data.context.data.name
    );

    this.form.patchValue({ comment: this.data.context.data.comment });

    if (!data.context.allowEditing) {
      this.form.disable();
    }
  }

  canDeactivate(): boolean | Observable<boolean> {
    return !this.form.dirty;
  }

  save(): Observable<boolean> {
    this.onDone();
    return of(false);
  }

  onDone(): void {
    if (!this.form.dirty) {
      this.dialogRef.close();
      return;
    }

    this.dialogRef.close({ comment: this.form.value.comment?.trim() });
  }

  onCancel(): void {
    if (!this.form.dirty) {
      this.dialogRef.close();
      return;
    }

    canDeactivateUnsavedChanges(this, this._dialog)
      .pipe(
        untilDestroyed(this),
        filter((val) => !!val),
        tap(() => {
          this.dialogRef.close();
        })
      )
      .subscribe();
  }

  protected activateComponent(): void {
    this._prevGuardedComponent =
      this.activatedComponentService.activatedComponent.value;
    this.activatedComponentService.activatedComponent.next(this);
  }

  protected deactivateComponent(): void {
    this.activatedComponentService.activatedComponent.next(
      this._prevGuardedComponent
    );
  }
}
